import axios from "axios";
import { config } from "./config";
import { handleTokenExpiration } from "./authUtils";

const API_BASE_URL = config.apiBaseUrl;

/**
 * Single source of truth for retrieving the auth token.
 * Reads from OfficeRuntime.storage where Auth0 stores it after login.
 */
export async function getIdToken(): Promise<string> {
  const token = await OfficeRuntime.storage.getItem("token");
  if (!token) {
    throw new Error("Token is not configured. Please login again.");
  }
  return token;
}

/**
 * Returns the current page/feature name from the hash route.
 * Used by both the Axios interceptor and standalone fetch calls
 * to set the X-Page-Source header for backend error logging.
 */
export function getPageSource(): string {
  const hash = window.location.hash || "";
  return hash.replace(/^#\/?/, "").split("/")[0] || "dashboard";
}

/**
 * Centralized Axios instance with interceptors for:
 * - Automatic auth token injection (request)
 * - 401 handling + error normalization (response)
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── Request Interceptor ──────────────────────────────────────────────
// Injects Bearer token on every outgoing request.
apiClient.interceptors.request.use(async (requestConfig) => {
  const token = await getIdToken();
  requestConfig.headers.Authorization = `Bearer ${token}`;

  // Send current page/feature name so backend can log which page triggered errors
  requestConfig.headers["X-Page-Source"] = getPageSource();

  return requestConfig;
});

// ── Response Interceptor ─────────────────────────────────────────────
// Handles 401 (session expiry) and normalises error shape.
apiClient.interceptors.response.use(
  (response) => response, // success passthrough
  async (error) => {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        await handleTokenExpiration("Session expired");
        return Promise.reject(new Error("Token is not configured. Please login again."));
      }

      // Normalise backend error detail into a readable message
      const detail =
        (typeof data?.detail === "string" && data.detail) ||
        (Array.isArray(data?.detail) && data.detail.join(", ")) ||
        data?.message ||
        error.message;

      return Promise.reject(Object.assign(new Error(detail), { status, responseData: data }));
    }

    // Network errors / timeouts (no response object)
    return Promise.reject(error);
  }
);

export default apiClient;
